import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { Button } from 'components/form/Button';
import { SmallModal } from 'components/Modals/SmallModal';
import { useLogger } from 'hook/error.hook';
import { useComplexState, useViewModalWatcher } from 'hook/helper.hook';
import React, { useState } from 'react';
import { ProfileDataItem } from 'pages/Profile/components/ProfileDataItem/ProfileDataItem';
import Label from 'components/form/Label/Label';
import { AddressForm } from './AddressForm';
import {
  CLICK,
  VIEW_POPUP,
} from 'externals/_tracking/types/eventTypes.constants';
import { isEnrolledInCash } from 'helpers/sesameCash.helpers';
import { startCognigyChat } from 'externals/_misc/cognigyChat';

const selectRecommendedAddress = recommendedAddressResponse => {
  const addressMetadataList = recommendedAddressResponse?.primaryAddress ?? [];
  let addressMetadata = {};

  if (addressMetadataList?.length > 0) addressMetadata = addressMetadataList[0];

  return addressMetadata;
};

const SuggestedAddressModal = props => {
  const addressMetadata = selectRecommendedAddress(
    props?.recommendedAddressResponse,
  );
  const { city, state, unit, zip, street } = addressMetadata;
  return (
    <SmallModal
      onCancel={props.onCloseRecommendationModal}
      visible={props?.showRecommendationConfirmation}
      footer={
        <FlexContainer className="flex-row t-flex-column  justify-end">
          <Button mrphType="link" onClick={props?.onClickTryAgain}>
            Try Again
          </Button>
          <div className="padB8" />
          <Button mrphType="primary" onClick={props?.onClickAccept}>
            Use Suggested Address
          </Button>
        </FlexContainer>
      }
    >
      <Label type="H4" typeHeader={'h4'} className="padB16">
        We found a similar address
      </Label>
      <Label type="text-16">
        Is this one correct? {street}
        {unit ? ' ' + unit : ''}, {city}, {state} {zip}
      </Label>
    </SmallModal>
  );
};

const AddressChangeNoMatchTuModal = props => {
  return (
    <SmallModal
      onCancel={props.onCloseAddressChangeNoMatchModal}
      visible={props?.showAddressChangeNoMatchModal}
      footer={false}
      width="480px"
    >
      <Label type="H3" typeHeader={'h3'} className="padB16">
        More Info Required
      </Label>
      <Label className="text-16">
        Please chat in to our customer support to change your address.
      </Label>
      <Label className="margTB20">
        <Button
          mrphType="primary"
          onClick={() => {
            props?.onClickChatSupport(props);
          }}
        >
          Chat with Support
        </Button>
      </Label>
    </SmallModal>
  );
};

export const Address = ({
  addressForm,
  isEditParent,
  setIsEditParent,
  saveData,
  disableAddressFromBeingEditable,
  isDisabledAptNumber,
  fullWidth,
  userInfo,
  ...props
}) => {
  const logger = useLogger({ name: 'Address' });
  const [isEditInternal, setIsEditInternal] = useState(false);
  let isEdit = isEditParent ? isEditParent : isEditInternal;
  let setIsEdit = setIsEditParent ? setIsEditParent : setIsEditInternal;

  const [state, setState] = useComplexState({
    loading: false,
    recommendedAddressResponse: null,
    showRecommendationConfirmation: false,
    showAddressChangeNoMatchModal: false,
  });

  const modalAttr = {
    'Pop Up Name': 'Suggested Address',
  };
  useViewModalWatcher(modalAttr, state.showRecommendationConfirmation);

  const onCloseRecommendationModal = () => {
    setState({
      showRecommendationConfirmation: false,
    });
  };

  const onCloseAddressChangeNoMatchModal = () => {
    setState({
      showAddressChangeNoMatchModal: false,
    });
  };
  const onClickTryAgain = () => {
    props?.eventHandler(CLICK, {
      'Click Type': 'Try Again',
      ...modalAttr,
    });
    setState({
      showRecommendationConfirmation: false,
    });
  };
  const onClickAccept = () => {
    props?.eventHandler(CLICK, {
      'Click Type': 'Use Suggested Address',
      ...modalAttr,
    });
    setState({
      showRecommendationConfirmation: false,
    });
    const addressMetadata = selectRecommendedAddress(
      state?.recommendedAddressResponse,
    );
    addressForm.updateAddress(addressMetadata);
    onSaveHandle(null, addressMetadata);
  };
  let primaryAddress = addressForm.getFullAddress();
  const unit = primaryAddress.unit ? primaryAddress.unit : '';
  const streetAddress = `${primaryAddress.street} ${unit}`;

  const { Title, DisplayView, EditView } = ProfileDataItem;

  function onEditHandle(e) {
    e.preventDefault();
    props.eventHandler(CLICK, { 'Click Type': 'Edit Address' });
    setIsEdit(true);
  }

  function onCancelHandle(e) {
    e.preventDefault();
    props.eventHandler(CLICK, { 'Click Type': 'Cancel Address Update' });
    setIsEdit(false);
    addressForm.clearForm();
  }

  function onSaveHandle(e, addressOverride) {
    e?.preventDefault();
    props.eventHandler(CLICK, { 'Click Type': 'Save Address Update' });
    if (addressForm.isAddressValid()) {
      addressForm.clearError();
      if (state?.loading) {
        return;
      }
      setState({ loading: true });
      saveData(
        { primaryAddress: addressOverride ?? addressForm.getFullAddress() },
        'address',
      )
        .then(_ => {
          setIsEdit(false);
          addressForm.updateAddress(primaryAddress);
          setState({ loading: false });
        })
        .catch(err => {
          setState({ loading: false });
          if (
            isEnrolledInCash(userInfo) &&
            (err?.code === 'AV_1002' || err?.code === 'AV_1003')
          ) {
            setState({
              showAddressChangeNoMatchModal: true,
            });
          } else {
            if (err?.code === 'SU_1066') {
              setState({
                showRecommendationConfirmation: true,
                recommendedAddressResponse: err,
              });
            } else if (err?.code === 'AV_1002') {
              props?.eventHandler(VIEW_POPUP, {
                'Pop Up Name': 'Address Change Unsuccessful',
              });
              setIsEdit(false);
              addressForm.updateAddress(props?.userInfo?.primaryAddress);
              logger.showWarning(
                <b>We’re Verifying Your Address</b>,
                `We’re going to double-check that address, and we’ll give 
              you an update within 2 business days. If you recently changed your address 
              with the credit bureaus, it may take time to fully reflect here.`,
              );
              props?.updateAddressState();
            } else {
              addressForm.handleAddressError(err);
            }
          }
        });
    }
  }

  const onClickOpenCognigyChat = props => {
    startCognigyChat(props).catch(e => {
      logger.showError(e.message);
    });
  };

  let profileDataItemClass = props.classNameOverwrites?.profileDataItem;

  return (
    <ProfileDataItem
      datatestid="profileAddress"
      classNameOverwrites={profileDataItemClass?.profileDataItem}
    >
      <Title datatestid="addressLabel" labelType={profileDataItemClass?.title}>
        Address
      </Title>
      {!isEdit && (
        <DisplayView
          editEnabled={disableAddressFromBeingEditable}
          editHandle={onEditHandle}
          classNameOverwrites={props.classNameOverwrites?.displayView}
        >
          <Label datatestid="streetReadOnly">{streetAddress}</Label>
          <Label>
            <span data-testid="cityReadOnly" className="toTitleCase">
              {primaryAddress.city.toLowerCase()}
            </span>
            {', '}
            <span data-testid="stateReadyOnly">{primaryAddress.state}</span>
            {', '}
            <span data-testid="zipReadOnly">{primaryAddress.zip}</span>
          </Label>
        </DisplayView>
      )}
      {isEdit && (
        <EditView
          onCancelHandle={onCancelHandle}
          onSaveHandle={onSaveHandle}
          disabled={addressForm.dirtyDataEmpty || addressForm.isMissingData()}
          hideButtons={props.hideButtons}
          classNameOverwrites={props.classNameOverwrites?.editView}
        >
          <AddressForm
            addressForm={addressForm}
            hideBlockError
            classNameOverwrites={props.classNameOverwrites?.editView?.editForm}
            isDisabledAptNumber={isDisabledAptNumber}
            fullWidth={fullWidth}
          />
        </EditView>
      )}
      <SuggestedAddressModal
        {...state}
        onCloseRecommendationModal={onCloseRecommendationModal}
        onClickTryAgain={onClickTryAgain}
        onClickAccept={onClickAccept}
      />
      <AddressChangeNoMatchTuModal
        {...state}
        onClickChatSupport={onClickOpenCognigyChat}
        onCloseAddressChangeNoMatchModal={onCloseAddressChangeNoMatchModal}
        userInfo={userInfo}
      />
    </ProfileDataItem>
  );
};
